import React, { useId } from 'react';
import { Mail } from 'lucide-react';
import gmailIcon from '../../../assets/email-providers/gmail.svg';
import outlookIcon from '../../../assets/email-providers/microsoftoutlook.svg';
import icloudIcon from '../../../assets/email-providers/icloud.svg';
import yahooIcon from '../../../assets/email-providers/yahoo.svg';
import protonIcon from '../../../assets/email-providers/protonmail.svg';
import AgeInput from '../inputs/AgeInput';
import LocationAutocomplete from '../inputs/LocationAutocomplete';
import DeviceSelector from '../inputs/DeviceSelector';
import { useAnalytics } from '../../../hooks/useAnalytics';

const DemographicsStep = ({ data = {}, onChange, disabled = false, question = 0, onNext, onLocationSelected }) => {
  const { age = '', location = '', device = '', emailProvider = '' } = data;
  const analytics = useAnalytics();
  const fieldId = useId();

  if (question === 0) return (
    <div>
      <h3 tabIndex={-1} className="survey-heading text-2xl mb-4">
        <label htmlFor={`${fieldId}-age`}>How old are you?</label>
      </h3>
      <AgeInput id={`${fieldId}-age`} value={age} onChange={newAge => onChange({ ...data, age: newAge })}
        onNext={onNext} disabled={disabled} placeholder="Your age" />
    </div>
  );

  if (question === 1) return (
    <div>
      <h3 tabIndex={-1} className="survey-heading text-2xl mb-4">
        <label htmlFor={`${fieldId}-city`}>Where are you based?</label>
      </h3>
      <LocationAutocomplete id={`${fieldId}-city`} value={location}
        onChange={newLocation => onChange({ ...data, location: newLocation, locationData: null })}
        onSelect={locationData => {
          onChange({ ...data, location: locationData.full_name, locationData });
          onLocationSelected?.();
        }}
        disabled={disabled} placeholder="Start typing your city..." />
    </div>
  );

  if (question === 2) return (
    <div>
      <h3 id={`${fieldId}-device`} tabIndex={-1} className="survey-heading text-2xl mb-4">What's your main device?</h3>
      <div role="group" aria-labelledby={`${fieldId}-device`}>
        <DeviceSelector value={device} disabled={disabled} onChange={newDevice => {
          onChange({ ...data, device: newDevice });
          if (newDevice) analytics.trackDeviceSelect(newDevice, 1);
        }} />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h3 id={`${fieldId}-provider`} tabIndex={-1} className="survey-heading text-2xl font-medium mb-2 text-white">
          Where do you usually receive your booking confirmations and tickets?
        </h3>
        <p className="text-sm text-white/60">Choose one option</p>
      </div>
      <div className="space-y-2" role="group" aria-labelledby={`${fieldId}-provider`}>
        {[
          ['gmail', 'Gmail', gmailIcon], ['outlook', 'Outlook', outlookIcon], ['icloud', 'iCloud', icloudIcon],
          ['yahoo', 'Yahoo', yahooIcon], ['proton', 'Proton', protonIcon], ['other', 'Other', null]
        ].map(([value, label, icon]) => {
          const selected = emailProvider === value;
          return (
            <button key={value} type="button" disabled={disabled}
              onClick={() => onChange({ ...data, emailProvider: value })}
              aria-label={`${selected ? 'Selected:' : 'Select'} ${label}`}
              aria-pressed={selected}
              className={`w-full flex items-center gap-3 text-left px-4 py-3.5 rounded-lg border transition-all duration-200 text-white/90 hover:text-white border-white/20 ${selected ? 'bg-white/15 border-white/30' : 'hover:bg-white/10'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
              {icon
                ? <img src={icon} alt="" aria-hidden="true" width={20} height={20} className="h-5 w-5 shrink-0" />
                : <Mail aria-hidden="true" size={20} className="shrink-0 text-white" />}
              <span className="text-[15px] leading-relaxed">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DemographicsStep;
